/**
 * Cliente de la API.
 *
 * ---------------------------------------------------------------------------
 * POR QUE LOS TIPOS SE REDECLARAN AQUI
 *
 * `@freight-bridge/api` exporta estos mismos tipos, pero importarlos arrastraria
 * el modulo entero --y con el `pg`-- al paquete del navegador. Asi que se
 * redeclaran, con dos consecuencias que conviene tener presentes: son una copia
 * que puede quedarse atras, y el compilador no avisara. Lo que si avisa es la
 * pantalla, porque un campo que deja de venir se pinta como ausente y no como
 * cero: ver `AUSENTE` en formato.ts.
 *
 * Los montos son `string` en todos los tipos de este archivo, y no es un
 * descuido: la API los serializa asi para que no pierdan precision al cruzarla
 * [CLAUDE.md, Dinero]. Convertirlos a `number` en el borde deshace eso. No hay
 * un solo `number` con dinero dentro en toda la interfaz.
 * ---------------------------------------------------------------------------
 */

/**
 * Todo cuelga de `/api`, que el servidor de desarrollo reenvia a la API. Asi la
 * pantalla y sus peticiones salen del mismo origen: sin CORS, sin credenciales
 * cruzadas, y un solo puerto que reenviar en Codespaces.
 */
const BASE = '/api';

export type CodigoError =
  | 'ENTRADA_INVALIDA'
  | 'DESAMBIGUACION_REQUERIDA'
  | 'SIN_RESOLVER'
  | 'TARIFA_NO_COTIZABLE'
  | 'EQUIPO_NO_OFRECIDO'
  | 'RECURSO_NO_ENCONTRADO'
  | 'RUTA_NO_ENCONTRADA'
  | 'ERROR_INTERNO'
  /** No hay sesion, o caduco. Cualquier ruta menos el login puede devolverlo. */
  | 'NO_AUTENTICADO'
  /** Hay sesion y le falta rol. No se arregla volviendo a entrar. */
  | 'PROHIBIDO'
  /** Solo del login. Nunca dice si el correo existe. */
  | 'CREDENCIALES_INVALIDAS'
  /** No es de la API: la peticion no llego a salir o no volvio. */
  | 'SIN_RED';

export interface CampoAmbiguo {
  readonly campo: 'pol' | 'pod' | 'equipo';
  readonly valor: string;
  readonly motivo:
    | 'NOMBRE_AMBIGUO'
    | 'ALIAS_SIN_CONFIRMAR'
    | 'ALIAS_MULTIPLE'
    | 'SIN_RESOLVER'
    | 'TIPO_INCOMPATIBLE';
  readonly mensaje: string;
  readonly opciones: readonly OpcionAmbigua[];
}

/** Una candidata de desambiguacion. Las de ubicacion traen pais; las de equipo,
 *  codigo. La pantalla pinta lo que haya. */
export interface OpcionAmbigua {
  readonly id?: string;
  readonly locode?: string | null;
  readonly iata?: string | null;
  readonly nombre?: string | null;
  readonly pais?: string;
  readonly tipo?: string;
  readonly codigo?: string;
  readonly teu?: string;
  readonly modo?: string;
}

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    readonly codigo: CodigoError,
    mensaje: string,
    /** [errores.ts] `campos` es el contrato de DESAMBIGUACION_REQUERIDA: una
     *  entrada por campo que hay que volver a mandar. */
    readonly campos?: readonly CampoAmbiguo[],
    readonly detalle?: Record<string, unknown>,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }
}

interface CuerpoError {
  error?: string;
  mensaje?: string;
  campos?: CampoAmbiguo[];
  detalle?: Record<string, unknown>;
}

/**
 * Que hacer cuando la API dice que ya no hay sesion.
 *
 * Lo registra `app.ts` al arrancar y sirve para una sola cosa: llevar la
 * pantalla al login cuando la sesion muere MIENTRAS se trabaja --caduco, la
 * cerraron desde otra pestaña, un ADMIN dio de baja al usuario o le reseteo la
 * contrasena--. Sin esto, cada accion devolveria un error distinto y la
 * pantalla parecerian rota en vez de cerrada.
 *
 * Se avisa SOLO con `NO_AUTENTICADO`. El 401 del login es
 * `CREDENCIALES_INVALIDAS` y no significa que se haya perdido nada: mandar la
 * pantalla al login estando ya en el login borraria lo que la persona acaba de
 * escribir.
 */
let avisarSesionPerdida: (() => void) | null = null;

export function alPerderSesion(cb: () => void): void {
  avisarSesionPerdida = cb;
}

async function pedir<T>(ruta: string, init?: RequestInit): Promise<T> {
  let respuesta: Response;
  try {
    respuesta = await fetch(`${BASE}${ruta}`, {
      ...init,
      // Explicito aunque sea el valor por defecto de `fetch`: la sesion viaja en
      // una cookie HttpOnly [services/api/src/sesion.ts] y esta linea es lo
      // unico que dice, en toda la pantalla, que las peticiones llevan
      // credenciales. `same-origin` y no `include` porque la API se sirve bajo
      // `/api` del mismo origen; `include` solo haria falta con CORS, que es
      // justo lo que el proxy existe para no necesitar.
      credentials: 'same-origin',
    });
  } catch (causa) {
    // La API caida y la API que responde 500 no se arreglan igual: una se
    // levanta, la otra se depura. El codigo las separa.
    throw new ErrorApi(0, 'SIN_RED', causa instanceof Error ? causa.message : 'fetch');
  }

  const texto = await respuesta.text();
  let json: unknown;
  try {
    json = texto === '' ? {} : JSON.parse(texto);
  } catch {
    throw new ErrorApi(
      respuesta.status,
      'ERROR_INTERNO',
      `La API devolvio algo que no es JSON (${respuesta.status}).`,
    );
  }

  if (!respuesta.ok) {
    const c = json as CuerpoError;
    const codigo = (c.error ?? 'ERROR_INTERNO') as CodigoError;
    if (codigo === 'NO_AUTENTICADO') avisarSesionPerdida?.();
    throw new ErrorApi(
      respuesta.status,
      codigo,
      c.mensaje ?? `La API devolvio ${respuesta.status}.`,
      c.campos,
      c.detalle,
    );
  }

  return json as T;
}

function postJson<T>(ruta: string, cuerpo: unknown): Promise<T> {
  return pedir<T>(ruta, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(cuerpo),
  });
}

// ---------------------------------------------------------------------------
// Sesion
//
// La cookie no se toca desde aqui. Es `HttpOnly`: `document.cookie` no la ve y
// esta pantalla no podria leerla aunque quisiera, que es exactamente el punto
// [services/api/src/sesion.ts]. Lo unico que se hace es pedir, y el navegador
// la adjunta solo.
// ---------------------------------------------------------------------------

export type RolUsuario = 'AGENTE' | 'SUPERVISOR' | 'OPERADOR' | 'ADMIN';

/**
 * Lo que quien entro puede hacer.
 *
 * Llega RESUELTO del servidor [services/api/src/permisos.ts] y esta pantalla
 * no lo deduce del rol. La alternativa --una copia de la tabla de capacidades
 * en el navegador-- son dos tablas que se separan en cuanto alguien toca una
 * sola, y el sintoma seria una zona que se ensena y contesta 403 al entrar, o
 * peor, una que existe y nadie encuentra.
 *
 * No es el candado. El candado es la API, que exige la capacidad en cada ruta;
 * esta lista solo decide que se dibuja en el menu. Recortarla desde la consola
 * del navegador esconde zonas, no abre ninguna.
 */
export type Capacidad =
  | 'catalogo.consultar'
  | 'cotizar'
  | 'cotizaciones.ver'
  | 'cotizaciones.ver.equipo'
  | 'rfq.gestionar'
  | 'margenes.gestionar'
  | 'tarifas.gestionar'
  | 'proveedores.gestionar'
  | 'usuarios.gestionar';

export interface UsuarioSesion {
  readonly id: string;
  readonly nombre: string;
  readonly email: string;
  readonly rol: RolUsuario;
  /** [CLAUDE.md, Visibilidad] Si los montos viajan hasta esta persona. La
   *  pantalla NO lo consulta para decidir que esconde --pinta los campos que
   *  llegaron-- sino para explicar por que falta una columna. */
  readonly ve_costo: boolean;
  readonly capacidades: readonly Capacidad[];
  /**
   * La contrasena la puso otra persona y todavia la conoce [migracion 0032].
   *
   * Mientras sea `true` la API contesta 409 CAMBIO_DE_CONTRASENA_PENDIENTE a
   * todo menos al cambio, asi que la pantalla no pinta la aplicacion: pinta
   * esa unica cosa. No es que se esconda el resto --el resto no funcionaria--.
   */
  readonly debe_cambiar_contrasena: boolean;
}

export interface Sesion {
  readonly usuario: UsuarioSesion;
  readonly expira_en: string;
}

export const iniciarSesion = (email: string, contrasena: string): Promise<Sesion> =>
  postJson('/sesion', { email, contrasena });

/** Quien soy. Devuelve 401 `NO_AUTENTICADO` si no hay sesion; quien llama
 *  decide, y en el arranque eso significa pintar el login. */
export const leerSesion = (): Promise<Sesion> => pedir('/sesion');

export const cerrarSesion = (): Promise<{ cerrada: boolean }> =>
  pedir('/sesion', { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Gestion de usuarios. Solo ADMIN: la API responde 403 a los demas.
// ---------------------------------------------------------------------------

export interface UsuarioAdmin {
  readonly id: string;
  readonly nombre: string;
  readonly email: string;
  readonly rol: RolUsuario;
  readonly ve_costo: boolean;
  readonly activo: boolean;
  /** Todavia no ha elegido su contrasena: la que tiene se la dieron. */
  readonly debe_cambiar_contrasena: boolean;
  /** Si puede entrar por la via propia. `false` en el ADMIN sembrado por la
   *  migracion mientras nadie corra `npm run contrasena`, y en quien algun dia
   *  entre solo por Entra ID. */
  readonly tiene_contrasena: boolean;
  readonly contrasena_actualizada_en: string | null;
  readonly sesiones_abiertas: number;
}

export const listarUsuariosAdmin = (): Promise<{ usuarios: UsuarioAdmin[] }> =>
  pedir('/usuarios');

/**
 * Alta de usuario.
 *
 * `contrasena_temporal` llega EN CLARO y solo aqui: no se guarda en ningun
 * sitio --lo que queda en la base es su hash-- y no hay forma de volver a
 * verla. Es `null` cuando la eligio quien llamaba, porque entonces ya la sabe.
 */
export interface UsuarioCreado {
  readonly usuario: UsuarioAdmin;
  readonly contrasena_temporal: string | null;
}

export const crearUsuarioAdmin = (cuerpo: unknown): Promise<UsuarioCreado> =>
  postJson('/usuarios', cuerpo);

/** Alta en lote. Todo o nada: si una fila falla, no se creo ninguno. */
export const crearUsuariosEnLote = (
  usuarios: readonly unknown[],
): Promise<{ creados: UsuarioCreado[] }> => postJson('/usuarios/lote', { usuarios });

/**
 * Cambiar la propia contrasena.
 *
 * Cuelga de `/sesion` y no de `/usuarios/:id`: el id sale de la sesion, asi que
 * no existe la forma de pedirlo para otro. Se exige la actual aunque quien la
 * manda acabe de entrar con ella --una sesion abierta en un navegador sin
 * bloquear es justo el caso que esto para--.
 */
export const cambiarContrasenaPropia = (
  actual: string,
  nueva: string,
): Promise<{ cambiada: true; sesiones_cerradas: number }> =>
  postJson('/sesion/contrasena', { actual, nueva });

export const cambiarEstadoUsuario = (
  id: string,
  activo: boolean,
): Promise<{ usuario: UsuarioAdmin }> =>
  postJson(`/usuarios/${encodeURIComponent(id)}/estado`, { activo });

export interface Reseteo {
  readonly usuario: UsuarioAdmin;
  /** En claro y solo aqui, igual que en el alta. `null` cuando la eligio quien
   *  llamaba. */
  readonly contrasena_temporal: string | null;
  /** Cuantas sesiones se cerraron al cambiar la contrasena. Se pinta: es la
   *  confirmacion de que surtio efecto ahora y no cuando caduque algo. */
  readonly sesiones_cerradas: number;
}

/** Sin `contrasena`, la genera el servidor y la devuelve una vez. */
export const resetearContrasenaUsuario = (
  id: string,
  contrasena: string,
): Promise<Reseteo> =>
  postJson(
    `/usuarios/${encodeURIComponent(id)}/contrasena`,
    contrasena === '' ? {} : { contrasena },
  );

// ---------------------------------------------------------------------------
// Listas de apoyo
// ---------------------------------------------------------------------------

export interface Tercero {
  readonly id: string;
  readonly nombre: string;
  readonly pais: string | null;
  readonly identificado: boolean;
}

export interface EquipoCatalogo {
  readonly codigo: string;
  readonly nombre: string;
  readonly teu: string;
  readonly modo: string;
}

export interface Sugerencia {
  readonly id: string;
  readonly locode: string | null;
  readonly iata: string | null;
  readonly nombre: string;
  readonly pais: string;
  readonly tipo: string;
  readonly via: 'LOCODE' | 'IATA' | 'ALIAS' | 'NOMBRE';
  readonly requiere_confirmacion: boolean;
  /**
   * De que capa del catalogo sale.
   *
   * `CURADA` son las 77 ubicaciones del ejercicio de tarifas: las que tienen
   * tarifas detras. Todo lo demas es la capa de referencia --el catalogo
   * mundial UN/LOCODE-- y se marca en la lista, porque elegir una es elegir un
   * puerto contra el que hoy no hay nada cargado.
   */
  readonly procedencia: Procedencia;
  readonly latitud: number | null;
  readonly longitud: number | null;
}

export const listarClientes = (): Promise<{ terceros: Tercero[] }> =>
  pedir('/terceros?rol=CLIENTE');

export const listarEquipos = (modo: string): Promise<{ equipos: EquipoCatalogo[] }> =>
  pedir(`/equipos?modo=${encodeURIComponent(modo)}`);

export interface RespuestaSugerencias {
  readonly ubicaciones: Sugerencia[];
  /** Hay coincidencias fuera del limite. Una lista recortada y una completa se
   *  ven igual, y la diferencia decide si el agente sigue escribiendo o
   *  concluye que su puerto no esta en el catalogo. */
  readonly hay_mas: boolean;
}

export const sugerirUbicaciones = (query: string): Promise<RespuestaSugerencias> =>
  pedir(`/ubicaciones?${query}`);

// ---------------------------------------------------------------------------
// Busqueda
// ---------------------------------------------------------------------------

/**
 * Las banderas que la API puede poner en una tarifa, como LISTA.
 *
 * Existe por lo mismo que `MOTIVOS_FUERA_DEL_TOTAL`: cada una se pinta
 * traducida, una clave que falte sale entre corchetes en pantalla y ni el
 * compilador ni una prueba de la pantalla la ven. Con la lista en tiempo de
 * ejecucion, `i18n.test.ts` exige que los dos diccionarios las cubran.
 *
 * Y ademas la usa la ayuda: el manual las enumera LEYENDO ESTAS MISMAS CLAVES,
 * no copiando los textos. Un manual con su propia redaccion de "embargo" se
 * separa del aviso que sale en la fila el dia que uno de los dos se corrija, y
 * entonces la ayuda explica algo que la pantalla ya no dice.
 */
export const BANDERAS = [
  'ALCANCE_NO_DECLARADO',
  'TERMINO_ENTREGA_NO_DECLARADO',
  'CARGOS_ESTIMADOS',
  'CARGOS_CONDICIONALES',
  'CARGOS_FUERA_DEL_TOTAL',
  'CONFIANZA_BAJA',
  'CARRIER_NO_IDENTIFICADO',
  'PROVEEDOR_NO_IDENTIFICADO',
  'MULTIMONEDA',
  'SIN_FLETE_PARA_EL_EQUIPO',
  'SUJETA_A_CONFIRMACION',
  'EMBARGO',
  'CONSULTA_CASO_A_CASO',
  'SOLO_CARTON',
  'ESPACIO_APROXIMADO',
  'DESPEGA_DE_OTRO_AEROPUERTO',
  'RESTRICCION_POR_BULTO',
  'BULTO_FUERA_DE_LIMITE',
  'COBRA_PESO_VOLUMETRICO',
] as const;

export type Bandera =
  | 'ALCANCE_NO_DECLARADO'
  | 'TERMINO_ENTREGA_NO_DECLARADO'
  | 'CARGOS_ESTIMADOS'
  | 'CARGOS_CONDICIONALES'
  | 'CARGOS_FUERA_DEL_TOTAL'
  | 'CONFIANZA_BAJA'
  | 'CARRIER_NO_IDENTIFICADO'
  | 'PROVEEDOR_NO_IDENTIFICADO'
  | 'MULTIMONEDA'
  | 'SIN_FLETE_PARA_EL_EQUIPO'
  | 'SUJETA_A_CONFIRMACION'
  // Las seis de abajo son del bloque aereo [migraciones 0022..0028]. Van en la
  // misma union y no en una aparte porque la API las manda en la misma lista:
  // partirlas obligaria a la pantalla a saber de antemano cual es de cual.
  | 'EMBARGO'
  | 'CONSULTA_CASO_A_CASO'
  | 'SOLO_CARTON'
  | 'ESPACIO_APROXIMADO'
  | 'DESPEGA_DE_OTRO_AEROPUERTO'
  | 'RESTRICCION_POR_BULTO'
  | 'BULTO_FUERA_DE_LIMITE'
  | 'COBRA_PESO_VOLUMETRICO';

/** Las que la ingesta leyo del propio documento, y que viajan con su literal.
 *  Espeja el ENUM `bandera_operativa` [migracion 0027]. [H86] */
export type BanderaOperativa =
  'EMBARGO' | 'CONSULTA_CASO_A_CASO' | 'SOLO_CARTON' | 'ESPACIO_APROXIMADO';

export interface AdvertenciaOperativa {
  readonly bandera: BanderaOperativa;
  /** El texto del proveedor, sin traducir ni limpiar [CLAUDE.md, Literales]. */
  readonly literal: string;
}

/** Un punto de un itinerario aereo. `latitud`/`longitud` pueden faltar: un
 *  aeropuerto sin coordenada se nombra y no se dibuja. */
export interface PuntoAereo {
  readonly iata: string | null;
  readonly nombre: string;
  readonly latitud: number | null;
  readonly longitud: number | null;
}

export interface EscalaAerea {
  readonly secuencia: number;
  /** AEREO o TERRESTRE: un pre-acarreo por carretera es una escala mas. [H82] */
  readonly modo: string;
  readonly origen: PuntoAereo;
  readonly destino: PuntoAereo;
  readonly carrier_literal: string | null;
  /** [H12] El nodo como lo escribio el proveedor cuando ofrecia mas de uno:
   *  "LAX/JFK/NLU". El origen/destino de arriba lleva el primero por regla
   *  mecanica, y esto es lo que impide afirmar una ruta que el correo dejaba
   *  abierta. */
  readonly alternativas: string | null;
}

export type MotivoNoApta = 'MEDIDA_EXCEDIDA' | 'PESO_POR_PIEZA_EXCEDIDO';

export interface IncumplimientoBulto {
  readonly motivo: MotivoNoApta;
  /** Metros o kilos segun el motivo, como cadena. */
  readonly declarado: string;
  readonly limite: string;
}

/**
 * Si los bultos declarados caben en esta tarifa. [H85]
 *
 * `NO_COMPROBABLE` no es `APTA`: significa que no habia con que comparar --la
 * busqueda no declaro bultos, o la tarifa no declara limites en numeros--. Se
 * dice con palabras, nunca dejando el hueco [H59].
 */
export interface AptitudBulto {
  readonly resultado: 'APTA' | 'NO_APTA' | 'NO_COMPROBABLE';
  readonly incumplimientos?: readonly IncumplimientoBulto[];
}

/**
 * La anatomia de una tarifa aerea: cuantos kilos se cobran, por que, a que
 * precio, por donde va y que dias sale.
 *
 * Presente SOLO en aereo. Ausente --no vacio-- en el resto de modos, que es lo
 * que permite a la pantalla decidir que tabla pinta sin preguntarle al modo.
 */
export interface BloqueAereo {
  readonly peso: {
    readonly real_kg: string;
    readonly volumetrico_kg: string;
    /** El mayor de los dos: es el que multiplica al precio. */
    readonly tarifable_kg: string;
    readonly manda: 'REAL' | 'VOLUMETRICO' | 'EMPATE';
    /** Centimetros cubicos por kilo. 6000 es la de IATA. */
    readonly relacion_volumetrica: string | null;
    /** [H80] Como lo escribio el proveedor: "1:500". */
    readonly relacion_volumetrica_literal: string | null;
  };
  readonly escalon: {
    /** Lo que rotulaba la columna del documento: "+45", "+1000". */
    readonly etiqueta: string;
    readonly desde_kg: string | null;
    /** Exclusivo: 100 kg justos pagan el escalon que empieza en 100. */
    readonly hasta_kg: string | null;
    /** Ausente sin `ve_costo`: es un precio del proveedor. */
    readonly precio_por_kg?: string;
  };
  readonly ruta: {
    readonly literal: string | null;
    /** [H82] De donde despega, cuando no es el aeropuerto de origen. Nulo = sale
     *  del propio origen. */
    readonly aeropuerto_salida: PuntoAereo | null;
    readonly escalas: readonly EscalaAerea[];
  };
  readonly frecuencia: {
    readonly literal: string | null;
    /** [H84] Dias ISO --1 lunes ... 7 domingo-- interpretados de la mascara.
     *  Nulo = no se interpreto, nunca "no opera": ahi manda el literal. */
    readonly dias: readonly number[] | null;
  };
  readonly restriccion_bulto: {
    readonly literal: string | null;
    readonly max_largo_m: string | null;
    readonly max_ancho_m: string | null;
    readonly max_alto_m: string | null;
    readonly max_peso_pieza_kg: string | null;
  } | null;
  readonly aptitud: AptitudBulto;
}

export interface CondicionCargo {
  readonly variable: string;
  readonly umbral_desde: string | null;
  readonly umbral_hasta: string | null;
  readonly unidad: string | null;
  readonly valor_texto: string | null;
  readonly automatica: boolean;
}

export interface CargoTarifa {
  readonly id: string;
  readonly codigo_cargo: string;
  readonly nombre: string;
  /** Literal del proveedor. No se traduce ni se limpia. */
  readonly descripcion_literal: string | null;
  readonly tramo: string;
  readonly base: string;
  readonly moneda: string;
  /** Ausentes cuando el usuario no ve costos: la API no los manda. No es que
   *  vengan nulos --no vienen-- y por eso son opcionales y no `| null`. */
  readonly monto?: string | null;
  readonly monto_min?: string | null;
  readonly monto_max?: string | null;
  readonly es_estimado: boolean;
  readonly inclusion: string;
  readonly direccion: string;
  readonly forma_pago: string;
  readonly pass_through: boolean;
  readonly break_desde: string | null;
  readonly break_hasta: string | null;
  readonly condiciones: readonly CondicionCargo[];
  readonly motivo_fuera_del_total?: string;
}

export interface TotalMoneda {
  readonly moneda: string;
  readonly costo_min: string;
  readonly costo_max: string;
  readonly ingreso: string;
  readonly neto_min: string;
  readonly neto_max: string;
}

export interface ResultadoTarifa {
  readonly rate_record_id: string;
  readonly proveedor: {
    readonly id: string | null;
    readonly nombre: string | null;
    readonly identificado: boolean;
  };
  readonly carrier: {
    readonly id: string | null;
    readonly nombre: string | null;
    readonly literal: string | null;
    readonly estado:
      | 'IDENTIFICADO'
      | 'CODIGO_PENDIENTE'
      | 'ENMASCARADO'
      | 'NO_DECLARADO'
      /** [H83] La celda nombra dos aerolineas ("DL/AA") y el proveedor no dice
       *  cual opera. No es un carrier que no reconozcamos: son dos. */
      | 'MULTIPLE';
    readonly tipo_contrato: string | null;
    readonly servicio_loop: string | null;
  };
  readonly transito: {
    readonly min_dias: number | null;
    readonly max_dias: number | null;
  };
  readonly dias_libres: {
    /** Nulo en aereo, donde no hay equipo. Los dias libres se declaran POR
     *  equipo [H42], asi que ahi vienen todos nulos. */
    readonly equipo: string | null;
    readonly demora: number | null;
    readonly detencion: number | null;
    readonly minimo: number | null;
  };
  readonly espacio: {
    readonly total: number | null;
    readonly disponible: number | null;
    readonly desde: string | null;
  };
  readonly alcance: string;
  readonly termino_entrega: string;
  readonly vigencia: {
    readonly tipo: string;
    readonly desde: string | null;
    readonly hasta: string | null;
    readonly etd: string | null;
  };
  /** Ausentes enteros cuando el usuario no ve costos. */
  readonly total?: TotalMoneda;
  readonly totales_por_moneda?: readonly TotalMoneda[];
  readonly cargos: readonly CargoTarifa[];
  readonly cargos_fuera_del_total: readonly CargoTarifa[];
  readonly confianza: string | null;
  readonly nota_revision: string | null;
  readonly campos_inferidos: Record<string, unknown>;
  readonly restriccion_mercancia: string | null;
  readonly condicion_comercial: string | null;
  readonly frecuencia_literal: string | null;
  readonly banderas: readonly Bandera[];
  /** [H86] Las advertencias del documento con el texto del proveedor. Vacia
   *  cuando no hay ninguna. Viaja sin `ve_costo`: un embargo no es un precio. */
  readonly advertencias_operativas: readonly AdvertenciaOperativa[];
  /** Solo en aereo. Ausente en el resto de modos, no vacio. */
  readonly aereo?: BloqueAereo;
}

export interface GrupoComparable {
  readonly alcance: string;
  readonly termino_entrega: string;
  readonly advertencia: string | null;
  readonly resultados: readonly ResultadoTarifa[];
}

/**
 * El puerto resuelto de un criterio.
 *
 * `latitud` y `longitud` salen de `ubicacion` en la base [migracion 0021]. Son
 * numeros y no cadenas --no son dinero-- y son `null` cuando no se sabe donde
 * esta el puerto, que es lo que hace que el mapa se retire en vez de pintar un
 * punto inventado [H59]. Ver vista/mapa.ts.
 */
export interface PuertoResuelto {
  readonly nombre: string;
  readonly pais: string;
  readonly locode: string | null;
  /** El designador IATA cuando el punto es un aeropuerto. Nulo en un puerto
   *  maritimo, y tambien en un aeropuerto que el catalogo no rotulo. */
  readonly iata?: string | null;
  readonly consultado: string;
  readonly procedencia: Procedencia;
  readonly latitud: number | null;
  readonly longitud: number | null;
}

/** De donde salio la fila del catalogo. Ver la nota en `Sugerencia`. */
export type Procedencia = 'CURADA' | 'UNLOCODE';

export interface CriterioResuelto {
  readonly pol: PuertoResuelto;
  readonly pod: PuertoResuelto;
  readonly modo: string;
  /** AUSENTE en aereo, no nulo: una busqueda aerea no tiene equipo que resolver
   *  porque una tarifa aerea no ofrece cajas. */
  readonly equipo?: {
    codigo: string;
    nombre: string;
    teu: string;
    consultado: string;
  };
  /** Presente solo en aereo, donde el peso ES parte del criterio: decide el
   *  escalon, y el escalon puede no existir. */
  readonly carga?: {
    readonly peso_kg: string;
    readonly volumen_m3: string;
    readonly bultos: string | null;
    readonly dim_largo_cm: string | null;
    readonly dim_ancho_cm: string | null;
    readonly dim_alto_cm: string | null;
  };
  readonly fecha_embarque: string;
}

/** Por que una tarifa de la ruta no llego a ser un resultado. El orden de
 *  `DiagnosticoVacio.causas` ya es el de lo que hay que arreglar antes: la
 *  pantalla lo respeta y no reordena por cantidad. */
/** Las causas de una busqueda vacia, como lista. Mismo motivo que `BANDERAS`:
 *  la ayuda las enumera y la prueba de idiomas las exige completas. */
export const CAUSAS_SIN_RESULTADOS = [
  'SIN_VERIFICAR',
  'FUERA_DE_FECHA',
  'EQUIPO_NO_OFRECIDO',
  'ESCALON_NO_OFRECIDO',
  'PESO_FUERA_DE_ESCALONES',
  'SIN_RELACION_VOLUMETRICA',
  'VENCIDA',
  'SIN_VIGENCIA_DECLARADA',
  'ZARPADA',
  'FUTURA',
  'CONSULTA_AL_MOMENTO',
] as const;

export type CausaSinResultados =
  | 'SIN_VERIFICAR'
  | 'FUERA_DE_FECHA'
  | 'EQUIPO_NO_OFRECIDO'
  /** [H81] El escalon que le toca a este peso lleva la barra del documento. Se
   *  arregla preguntando por otro peso, no firmando ni pidiendo un correo. */
  | 'ESCALON_NO_OFRECIDO'
  /** [H8] El peso queda por debajo del primer escalon. No se extrapola hacia
   *  abajo: seria inventar una tarifa. */
  | 'PESO_FUERA_DE_ESCALONES'
  /** [H80] La tarifa no declara con que convertir el volumen en kilos, y no se
   *  supone la de IATA. */
  | 'SIN_RELACION_VOLUMETRICA'
  | 'VENCIDA'
  | 'SIN_VIGENCIA_DECLARADA'
  | 'ZARPADA'
  | 'FUTURA'
  | 'CONSULTA_AL_MOMENTO';

export interface CausaVacio {
  readonly causa: CausaSinResultados;
  readonly tarifas: number;
  readonly ultima_vigencia: string | null;
}

/** Solo viene cuando `total_resultados` es 0. */
export interface DiagnosticoVacio {
  readonly tarifas_en_la_ruta: number;
  readonly causas: readonly CausaVacio[];
}

export interface RespuestaBusqueda {
  readonly criterio: CriterioResuelto;
  readonly total_resultados: number;
  readonly grupos: readonly GrupoComparable[];
  readonly nota_comparabilidad: string;
  /** Si los montos vinieron. Lo decide la API a partir de `usuario.ve_costo`;
   *  la pantalla solo lo usa para explicar por que falta una columna. */
  readonly ve_costo: boolean;
  readonly diagnostico?: DiagnosticoVacio;
}

export const buscarTarifas = (cuerpo: unknown): Promise<RespuestaBusqueda> =>
  postJson('/tarifas/busqueda', cuerpo);

// ---------------------------------------------------------------------------
// Cotizacion
// ---------------------------------------------------------------------------

export interface LineaCotizacion {
  readonly id: string;
  readonly codigo_cargo: string;
  readonly tramo: string;
  /** Literal del proveedor tal como llego a `opcion_linea.descripcion`. */
  readonly descripcion: string | null;
  readonly unidades: string;
  readonly venta: string;
  readonly inclusion: string;
  readonly direccion: string;
  readonly es_estimado: boolean;
  /** Ausentes cuando el usuario no ve costos. Lo decide
   *  `fn_opcion_linea_agente` en la base, no esta pantalla. */
  readonly costo?: string;
  readonly margen?: string;
}

/**
 * Por que un cargo no se pudo convertir en una linea con monto.
 *
 * ---------------------------------------------------------------------------
 * ES UNA LISTA Y NO SOLO UN TIPO, Y ESO ES LO QUE LA HACE UTIL
 *
 * Cada valor se pinta traducido --`motivoFuera.<VALOR>`-- y una clave que falte
 * NO rompe nada: el traductor devuelve `[motivoFuera.LO_QUE_SEA]` y eso es lo
 * que sale en pantalla, dentro de un documento que se manda a un cliente. Paso:
 * `ESCALON_NO_ELEGIDO` entro en la API con el aereo y nadie añadio su texto, y
 * ni el compilador ni las pruebas de la pantalla lo vieron.
 *
 * Con la lista en tiempo de ejecucion, `i18n.test.ts` recorre los valores y
 * exige que los dos diccionarios los cubran. Es el mismo trato que reciben las
 * capacidades en `permisos.ts`: la tabla se lee de un vistazo y es lo unico que
 * hay que tocar.
 *
 * Espeja `MotivoNoValorado` de services/api/src/cotizacion.ts, mas los dos que
 * añade la busqueda por su cuenta. Duplicado a proposito y marcado como tal: el
 * navegador no puede importar del servicio.
 * ---------------------------------------------------------------------------
 */
export const MOTIVOS_FUERA_DEL_TOTAL = [
  'CONDICIONADO',
  'BREAK_DE_PESO',
  'BASE_NO_DETERMINABLE',
  'MONEDA_DISTINTA',
  /** [H81] La barra del documento: a ese peso no hay tarifa. */
  'NO_OFRECIDO',
  /** [H8] Es la misma tarifa a otro peso, no un cargo distinto. */
  'ESCALON_NO_ELEGIDO',
  /** [Invariante 1][H62] Ya viene dentro del flete: no se suma. */
  'INCLUIDO_EN_FLETE',
  'EXCLUIDO_DECLARADO',
  /** [H48] Vino en rango y sin cotas utilizables. */
  'RANGO_SIN_COTAS',
] as const;

export type MotivoFueraDelTotal = (typeof MOTIVOS_FUERA_DEL_TOTAL)[number];

export interface CargoNoValorado {
  readonly codigo_cargo: string;
  readonly tramo: string;
  readonly descripcion: string | null;
  readonly motivo: string;
  readonly moneda: string;
  readonly condiciones?: readonly CondicionCargo[];
}

/**
 * Que es lo que viaja, tal como lo publica la API.
 *
 * Los campos que no aplican NO VIENEN --no vienen en `null`-- por la misma
 * regla que los montos [CLAUDE.md]: un hueco invita a pintar un guion donde no
 * hay nada que pintar. Por eso es un mapa suelto y no una interfaz con siete
 * opcionales: quien lo pinta pregunta por lo que hay.
 */
export type CargaPublicada = Record<string, unknown>;

export interface RespuestaCotizacion {
  readonly cotizacion: {
    readonly id: string;
    readonly folio: string;
    readonly estado: string;
    readonly idioma: string;
    readonly valida_hasta: string;
    readonly moneda_venta: string;
    readonly cliente_id: string;
    readonly agente_id: string;
    /** Que es lo que viaja [migracion 0033]. Los campos que no aplican no
     *  vienen: ver `CargaPublicada`. */
    readonly carga: CargaPublicada;
  };
  readonly opcion: {
    readonly id: string;
    readonly rate_record_id: string;
    readonly equipo: string;
    readonly unidades: number;
    readonly tasa_fx: string;
    readonly termino_entrega: string;
    readonly alcance: string;
    readonly transito_min_dias: number | null;
    readonly transito_max_dias: number | null;
    readonly dias_libres_demora: number | null;
    readonly dias_libres_detencion: number | null;
  };
  readonly lineas: readonly LineaCotizacion[];
  readonly totales: {
    readonly venta: string;
    readonly costo?: string;
    readonly margen?: string;
    readonly margen_pct?: string;
  };
  readonly requiere_aprobacion: boolean;
  readonly motivo_aprobacion: string | null;
  readonly piso_aplicado: string | null;
  readonly ve_costo: boolean;
  readonly tiene_estimados: boolean;
  readonly condiciones_a_revisar: readonly CargoNoValorado[];
  readonly cargos_no_valorados: readonly CargoNoValorado[];
}

export const crearCotizacion = (cuerpo: unknown): Promise<RespuestaCotizacion> =>
  postJson('/cotizaciones', cuerpo);

// ---------------------------------------------------------------------------
// RFQ saliente
//
// Ninguna de estas llamadas manda un correo. La API redacta el texto y registra
// que salio; el envio de verdad no existe todavia y es un punto de integracion
// marcado (Microsoft Graph). Ver services/api/src/rfq.ts.
// ---------------------------------------------------------------------------

export interface ContactoRfq {
  readonly id: string;
  readonly nombre: string | null;
  readonly email: string | null;
  readonly idioma: string | null;
  readonly recibe_rfq: boolean;
}

export interface ProveedorRfq {
  readonly id: string;
  readonly nombre: string;
  readonly pais: string | null;
  readonly identificado: boolean;
  /** 3 puerto, 2 pais, 1 region. Nula cuando su especialidad no cubre la ruta
   *  --o cuando no tiene ninguna declarada, que es `sin_especialidad`--. */
  readonly precision: number | null;
  readonly coincidencia: 'PUERTO' | 'PAIS' | 'REGION' | null;
  readonly sin_especialidad: boolean;
  /** Literal del proveedor: "China-LATAM maritimo". No se traduce. */
  readonly alcances: readonly string[];
  readonly contactos: readonly ContactoRfq[];
  readonly puede_recibir: boolean;
}

export interface CriterioRfq {
  readonly pol: { id: string; nombre: string; pais: string; locode: string | null };
  readonly pod: { id: string; nombre: string; pais: string; locode: string | null };
  readonly modo: string;
  readonly equipo: { codigo: string; nombre: string } | null;
}

export interface RespuestaProveedoresRfq {
  readonly criterio: CriterioRfq;
  readonly proveedores: readonly ProveedorRfq[];
}

export interface DestinatarioRfq {
  readonly id: string;
  readonly proveedor_id: string;
  readonly proveedor_nombre: string;
  readonly contacto_id: string | null;
  /** A donde salio, congelado al crear la solicitud. */
  readonly email: string;
  readonly nombre: string | null;
  readonly idioma: string;
  readonly precision_especialidad: number | null;
  readonly enviado_en: string | null;
}

export interface RespuestaRfq {
  readonly id: string;
  readonly proveedor_id: string;
  readonly proveedor_nombre: string;
  readonly respondida_en: string;
  readonly registrada_en: string;
  readonly rate_sheet_id: string | null;
  readonly rate_sheet_asunto: string | null;
  readonly sin_oferta: boolean;
  readonly nota: string | null;
}

export interface CorreoRfq {
  readonly idioma: string;
  readonly asunto: string;
  readonly cuerpo: string;
  readonly para: readonly { email: string; nombre: string | null }[];
}

export interface SolicitudRfq {
  readonly id: string;
  readonly folio: string;
  readonly estado: 'BORRADOR' | 'ENVIADA' | 'CON_RESPUESTAS' | 'CERRADA';
  readonly remitente: 'AGS' | 'FREIGHT_BRIDGE';
  readonly modo: string;
  readonly pol: { nombre: string; pais: string; locode: string | null };
  readonly pod: { nombre: string; pais: string; locode: string | null };
  readonly equipo: { codigo: string; nombre: string } | null;
  readonly unidades: number | null;
  readonly mercancia: string | null;
  readonly requisitos: string | null;
  /** Cadenas: vienen de `numeric` y no se convierten en el borde. */
  readonly peso_kg: string | null;
  readonly volumen_m3: string | null;
  readonly temperatura_c: string | null;
  readonly fecha_objetivo: string | null;
  readonly nota: string | null;
  readonly creada_por: string | null;
  readonly creada_en: string;
  readonly enviada_en: string | null;
  readonly cerrada_en: string | null;
}

export interface DetalleSolicitudRfq {
  readonly solicitud: SolicitudRfq;
  readonly destinatarios: readonly DestinatarioRfq[];
  readonly respuestas: readonly RespuestaRfq[];
  readonly correos: readonly CorreoRfq[];
}

export interface ResumenSolicitudRfq {
  readonly id: string;
  readonly folio: string;
  readonly estado: 'BORRADOR' | 'ENVIADA' | 'CON_RESPUESTAS' | 'CERRADA';
  readonly remitente: string;
  readonly modo: string;
  readonly pol_nombre: string;
  readonly pol_pais: string;
  readonly pol_locode: string | null;
  readonly pod_nombre: string;
  readonly pod_pais: string;
  readonly pod_locode: string | null;
  readonly equipo_codigo: string | null;
  readonly unidades: number | null;
  readonly mercancia: string | null;
  readonly fecha_objetivo: string | null;
  readonly creada_por_nombre: string | null;
  readonly creada_en: string;
  readonly enviada_en: string | null;
  readonly cerrada_en: string | null;
  readonly destinatarios: number;
  readonly respuestas: number;
  readonly sin_responder: number;
}

export const listarProveedoresRfq = (query: string): Promise<RespuestaProveedoresRfq> =>
  pedir(`/rfq/proveedores?${query}`);

export const crearSolicitudRfq = (cuerpo: unknown): Promise<DetalleSolicitudRfq> =>
  postJson('/rfq/solicitudes', cuerpo);

export const leerSolicitudRfq = (id: string): Promise<DetalleSolicitudRfq> =>
  pedir(`/rfq/solicitudes/${encodeURIComponent(id)}`);

/** Marca la solicitud como enviada. Lo dice el operador, no el sistema: hasta
 *  que exista Microsoft Graph, aqui no se manda ningun correo. */
export const marcarEnviadaRfq = (id: string): Promise<DetalleSolicitudRfq> =>
  postJson(`/rfq/solicitudes/${encodeURIComponent(id)}/envio`, {});

export const cerrarSolicitudRfq = (id: string): Promise<DetalleSolicitudRfq> =>
  postJson(`/rfq/solicitudes/${encodeURIComponent(id)}/cierre`, {});

export const registrarRespuestaRfq = (
  id: string,
  cuerpo: unknown,
): Promise<DetalleSolicitudRfq> =>
  postJson(`/rfq/solicitudes/${encodeURIComponent(id)}/respuestas`, cuerpo);

export const listarSolicitudesRfq = (
  todas: boolean,
): Promise<{
  solo_abiertas: boolean;
  solicitudes: ResumenSolicitudRfq[];
  hay_mas: boolean;
}> => pedir(`/rfq/solicitudes${todas ? '?estado=TODAS' : ''}`);

// ---------------------------------------------------------------------------
// Cola de altas de proveedor
//
// La otra mitad del formulario publico. Aqui no hay ni un monto: una solicitud
// de alta no lleva dinero dentro, asi que `ve_costo` no pinta nada --igual que
// en el RFQ--. `usuario_id` se manda por lo de siempre: para saber quien
// decidio. Ver services/api/src/portal.ts.
// ---------------------------------------------------------------------------

export interface MembresiaDeclarada {
  readonly tipo: string;
  readonly numero: string | null;
}

export interface SolicitudAlta {
  readonly id: string;
  readonly folio: string;
  readonly estado: 'PENDIENTE' | 'APROBADA' | 'RECHAZADA';
  /** Literales de quien solicita: se pintan tal como llegaron, en cualquier
   *  idioma o alfabeto [CLAUDE.md, Idioma, categoria 2]. */
  readonly empresa: string;
  readonly pais_literal: string;
  readonly sitio_web: string | null;
  readonly contacto_nombre: string;
  readonly contacto_email: string;
  readonly contacto_telefono: string | null;
  readonly idioma: string;
  readonly modos: readonly string[];
  readonly origenes: readonly string[];
  readonly destinos: readonly string[];
  readonly rutas_literal: string | null;
  readonly membresias: readonly MembresiaDeclarada[];
  readonly comentario: string | null;
  readonly recibida_en: string;
  readonly revisada_en: string | null;
  readonly revisada_por_nombre: string | null;
  readonly motivo_rechazo: string | null;
  readonly tercero_id: string | null;
  readonly tercero_nombre: string | null;
  /** Señales de duplicado. No bloquean nada: se enseñan y decide la persona. */
  readonly contacto_conocido_tercero_id: string | null;
  readonly contacto_conocido_nombre: string | null;
  readonly homonimos: readonly string[] | null;
  readonly licencias_tomadas: readonly string[] | null;
  readonly otras_pendientes: number;
}

/** Lo que se escribio en el directorio al aprobar. */
export interface CreadoAlta {
  readonly tercero: 'CREADO' | 'EXISTENTE';
  readonly tercero_id: string;
  readonly tercero_nombre: string;
  readonly contacto: 'CREADO' | 'COMPLETADO';
  readonly licencias: number;
  readonly licencias_omitidas: readonly {
    tipo: string;
    numero: string | null;
    motivo: 'SIN_NUMERO' | 'YA_REGISTRADA';
  }[];
  readonly especialidades: number;
  readonly especialidades_repetidas: number;
  readonly trades_no_reconocidos: readonly string[];
}

export interface DecisionAlta {
  readonly solicitud: SolicitudAlta;
  readonly creado?: CreadoAlta;
}

export const listarAltas = (
  todas: boolean,
): Promise<{
  solo_pendientes: boolean;
  solicitudes: SolicitudAlta[];
  hay_mas: boolean;
}> => pedir(`/solicitudes-proveedor${todas ? '?estado=TODAS' : ''}`);

export const aprobarAlta = (id: string, cuerpo: unknown): Promise<DecisionAlta> =>
  postJson(`/solicitudes-proveedor/${encodeURIComponent(id)}/aprobacion`, cuerpo);

export const rechazarAlta = (id: string, cuerpo: unknown): Promise<DecisionAlta> =>
  postJson(`/solicitudes-proveedor/${encodeURIComponent(id)}/rechazo`, cuerpo);

// ---------------------------------------------------------------------------
// Cotizaciones emitidas
//
// Hasta ahora una cotizacion se veia UNA vez, en la pantalla que la acababa de
// crear, y al recargar el navegador desaparecia. Estas dos rutas son volver a
// abrirla.
//
// Nada se recalcula: los montos y el veredicto de aprobacion salen como
// quedaron escritos [Invariante 5][migracion 0030]. Por eso
// `requiere_aprobacion` puede ser `null`, y `null` NO es `false`: es una opcion
// anterior a esa migracion, de la que no consta. La pantalla lo dice con
// palabras y no lo pinta como un "no" [H59].
//
// Que cotizaciones llegan aqui lo decide la API acotando por agente: sin
// `cotizaciones.ver.equipo` solo las propias. `solo_mias` viene en la respuesta
// para poder decirlo.
// ---------------------------------------------------------------------------

export interface ResumenCotizacion {
  /** Se ve en la lista porque una cotizacion de peligrosa no se busca igual que
   *  una general: es lo que hace que se distinga sin abrirla. */
  readonly naturaleza: string;
  readonly id: string;
  readonly folio: string;
  readonly estado: string;
  readonly creada_en: string;
  readonly valida_hasta: string;
  readonly moneda_venta: string;
  readonly idioma: string;
  readonly cliente: { readonly id: string; readonly nombre: string };
  readonly agente: {
    readonly id: string;
    readonly nombre: string;
    readonly propia: boolean;
  };
  /** `null` cuando la tarifa de origen ya no esta: `rate_record_id` es
   *  ON DELETE SET NULL y la cotizacion sigue siendo valida sin ella. */
  readonly ruta: string | null;
  readonly equipo: string | null;
  readonly total_venta: string | null;
  /** `null` = no consta. Ver la cabecera de este bloque. */
  readonly requiere_aprobacion: boolean | null;
  readonly opciones: number;
}

/* Una linea releida es exactamente una linea recien cotizada: las dos salen de
 * `fn_opcion_linea_agente` con las mismas columnas. Se reusa el tipo --y la
 * tabla que lo pinta-- en vez de declarar un gemelo que se separaria del
 * original la primera vez que alguien anada un campo a uno solo. */

export interface PuntoRuta {
  readonly nombre: string;
  readonly pais: string | null;
  readonly locode: string | null;
  /** Numeros y no cadenas: son el unico dato de esta API que no es dinero, y
   *  el mapa los quiere como numeros. Ver `PuertoResuelto`. */
  readonly latitud: number | null;
  readonly longitud: number | null;
}

export interface OpcionGuardada {
  readonly id: string;
  readonly rate_record_id: string | null;
  readonly equipo: string | null;
  readonly etiqueta: string | null;
  readonly transito_min_dias: number | null;
  readonly transito_max_dias: number | null;
  readonly dias_libres_demora: number | null;
  readonly dias_libres_detencion: number | null;
  readonly termino_entrega: string;
  readonly alcance: string;
  readonly tasa_fx: string;
  readonly seleccionada: boolean;
  readonly requiere_aprobacion: boolean | null;
  readonly motivo_aprobacion: string | null;
  readonly piso_aplicado: string | null;
  readonly tiene_estimados: boolean | null;
  readonly ruta: {
    readonly modo: string | null;
    readonly pol: PuntoRuta;
    readonly pod: PuntoRuta;
  } | null;
  /**
   * La anatomia aerea CONGELADA [migracion 0034].
   *
   * Ausente entera fuera del aereo: una opcion maritima no tiene un peso
   * tarifable que valga null, no tiene peso tarifable. Y nada se recalcula al
   * releer [Invariante 5]: estos son los numeros con los que salio el
   * documento, aunque el proveedor haya corregido su tarifa despues.
   *
   * No es `BloqueAereo`: lleva menos. La ruta, la frecuencia y los limites por
   * bulto describen la tarifa del catalogo, no lo que se congelo, y la
   * cotizacion no los guarda.
   */
  readonly aereo?: {
    readonly peso: {
      readonly real_kg: string;
      /** Derivado del volumen y la relacion congelados, con la funcion del
       *  motor. No es un recalculo de la cotizacion: las dos entradas son las
       *  de entonces. */
      readonly volumetrico_kg: string | null;
      readonly tarifable_kg: string;
      readonly manda: 'REAL' | 'VOLUMETRICO' | 'EMPATE';
      readonly relacion_volumetrica: string;
      readonly relacion_volumetrica_literal: string | null;
    };
    readonly escalon: {
      readonly etiqueta: string;
      readonly desde_kg: string | null;
      readonly hasta_kg: string | null;
      readonly precio_por_kg?: string;
    };
  };
  readonly totales: { readonly venta: string; readonly costo?: string };
  readonly lineas: readonly LineaCotizacion[];
}

export interface CotizacionGuardada {
  readonly cotizacion: {
    readonly id: string;
    readonly folio: string;
    readonly estado: string;
    readonly idioma: string;
    readonly creada_en: string;
    readonly enviada_en: string | null;
    readonly valida_hasta: string;
    readonly moneda_venta: string;
    readonly cliente: { readonly id: string; readonly nombre: string };
    readonly agente: {
      readonly id: string;
      readonly nombre: string;
      readonly propia: boolean;
    };
    /** Que viajaba, congelado con el documento. */
    readonly carga: CargaPublicada;
  };
  readonly opciones: readonly OpcionGuardada[];
  readonly ve_costo: boolean;
}

export const listarCotizaciones = (): Promise<{
  solo_mias: boolean;
  /** Hay cotizaciones fuera del corte. Ver `hay_mas` en la API: una lista
   *  recortada y una completa se ven igual, y esa diferencia decide si alguien
   *  rehace un documento que ya existe. */
  hay_mas: boolean;
  cotizaciones: ResumenCotizacion[];
}> => pedir('/cotizaciones');

export const leerCotizacionGuardada = (id: string): Promise<CotizacionGuardada> =>
  pedir(`/cotizaciones/${encodeURIComponent(id)}`);

// ---------------------------------------------------------------------------
// Las dos colas del catalogo
//
// Existian las dos y solo por terminal (`npm run verificar-sheet`,
// `npm run revisar-alias`). Firmar un sheet SI se hace desde la pantalla --es
// la misma transicion, con el mismo trigger detras--; confirmar un alias NO,
// porque es un UPDATE sobre el catalogo y eso se escribe como migracion
// [CLAUDE.md]. Por eso la respuesta de alias trae `se_despacha_por`: es la
// explicacion de por que esa cola no tiene boton.
// ---------------------------------------------------------------------------

export interface SheetPendiente {
  readonly rate_sheet_id: string;
  readonly message_id: string | null;
  readonly asunto: string | null;
  readonly remitente: string | null;
  readonly proveedor: string | null;
  readonly estado: string;
  readonly recibido_en: string;
  readonly ingresado_en: string;
  readonly records: number;
  readonly records_sin_flete: number;
  readonly apto_para_verificar: boolean;
  readonly motivo_bloqueo: string | null;
}

export interface Firma {
  readonly rate_sheet_id: string;
  readonly message_id: string | null;
  readonly estado: string;
  readonly verificado_en: string;
  readonly verificado_por: { readonly id: string; readonly nombre: string };
  readonly records_cotizables: number;
}

export interface AliasPendiente {
  readonly catalogo: string;
  readonly alias: string;
  readonly resuelve_a: string | null;
  readonly idioma: string | null;
  readonly confianza: string | null;
  readonly pais_hint: string | null;
  readonly proveedor: string | null;
  readonly estado: 'PROPUESTO' | 'SIN_RESOLVER';
  readonly expansiones_bloqueadas: number;
  readonly filas_afectadas: number;
}

export const listarColaSheets = (): Promise<{ sheets: SheetPendiente[] }> =>
  pedir('/rate-sheets/cola');

export const verificarSheet = (id: string): Promise<Firma> =>
  postJson(`/rate-sheets/${encodeURIComponent(id)}/verificacion`, {});

export const listarColaAlias = (): Promise<{
  alias: AliasPendiente[];
  se_despacha_por: string;
}> => pedir('/alias/cola');

// ---------------------------------------------------------------------------
// Directorio de proveedores
//
// Solo lectura. Se entra por `/apply` y su cola de revision, que es donde estan
// las reglas; esto es lo que hay dentro. Ver services/api/src/proveedores.ts.
// ---------------------------------------------------------------------------

export interface ContactoDirectorio {
  readonly id: string;
  readonly nombre: string | null;
  readonly email: string | null;
  readonly telefono: string | null;
  readonly departamento: string | null;
  readonly idioma: string | null;
  readonly recibe_rfq: boolean;
}

export interface EspecialidadDirectorio {
  readonly modo: string;
  readonly origen: string;
  readonly destino: string;
  readonly alcance_literal: string | null;
}

export interface ProveedorDirectorio {
  readonly id: string;
  readonly nombre: string;
  readonly pais: string | null;
  readonly identificado: boolean;
  readonly roles: readonly string[];
  readonly contactos: readonly ContactoDirectorio[];
  readonly especialidades: readonly EspecialidadDirectorio[];
  /** Lo que hay que mirar: sin ningun contacto con correo no recibe un RFQ, y
   *  en la lista se ve igual que los demas. */
  readonly puede_recibir: boolean;
  readonly rate_sheets: number;
}

export const listarDirectorio = (
  q: string,
): Promise<{
  q: string | null;
  total: number;
  hay_mas: boolean;
  proveedores: ProveedorDirectorio[];
}> => pedir(`/proveedores${q.trim() === '' ? '' : `?q=${encodeURIComponent(q.trim())}`}`);

// ---------------------------------------------------------------------------
// Reglas de margen y pisos
//
// NO SE EDITAN: SE SUSTITUYEN. `opcion_linea.regla_id` apunta a la fila y es la
// unica respuesta a "por que este precio" [Invariante 10], asi que cambiar el
// valor de una regla en sitio haria mentir a todas las lineas ya cotizadas.
// Guardar es desactivar la vieja e insertar una nueva con `sustituye_a`
// apuntando a ella; la cadena de sustituciones es el registro de cambios.
//
// La `especificidad` la calcula el servidor con la misma funcion que usa el
// motor al cotizar, y `pesos` viaja para poder desglosarla en pantalla. Esta
// pantalla no la calcula: dos copias se separarian, y el sintoma seria una
// interfaz que dice que manda una regla y un motor que aplica otra.
// ---------------------------------------------------------------------------

export interface ReglaMargen {
  readonly id: string;
  readonly descripcion: string;
  readonly tipo: string;
  readonly valor: string;
  readonly modo: string | null;
  readonly tramo: string | null;
  readonly codigo_cargo: string | null;
  readonly pol: { readonly id: string; readonly nombre: string } | null;
  readonly pod: { readonly id: string; readonly nombre: string } | null;
  readonly trade: string | null;
  readonly cliente: { readonly id: string; readonly nombre: string } | null;
  readonly activa: boolean;
  readonly vigencia_desde: string | null;
  readonly vigencia_hasta: string | null;
  readonly especificidad: number;
  readonly creada_en: string;
  readonly creada_por: string | null;
  readonly sustituye_a: string | null;
  readonly desactivada_en: string | null;
  readonly desactivada_por: string | null;
}

export interface PisoMargenVista {
  readonly id: string;
  readonly descripcion: string;
  readonly margen_min_pct: string | null;
  readonly margen_min_abs: string | null;
  readonly modo: string | null;
  readonly trade: string | null;
  readonly cliente: { readonly id: string; readonly nombre: string } | null;
  readonly activa: boolean;
  readonly creada_en: string;
  readonly creada_por: string | null;
  readonly sustituye_a: string | null;
  readonly desactivada_en: string | null;
  readonly desactivada_por: string | null;
}

export interface PesosCascada {
  readonly clienteId: number;
  readonly polPod: number;
  readonly trade: number;
  readonly codigoCargo: number;
  readonly tramo: number;
  readonly modo: number;
}

export interface Margenes {
  readonly reglas: ReglaMargen[];
  readonly pisos: PisoMargenVista[];
  readonly pesos: PesosCascada;
  readonly incluye_inactivas: boolean;
}

export const listarMargenes = (todas: boolean): Promise<Margenes> =>
  pedir(`/margenes${todas ? '?estado=TODAS' : ''}`);

export const crearReglaMargen = (cuerpo: unknown): Promise<{ regla: ReglaMargen }> =>
  postJson('/margenes/reglas', cuerpo);

export const sustituirReglaMargen = (
  id: string,
  cuerpo: unknown,
): Promise<{ anterior: ReglaMargen; nueva: ReglaMargen }> =>
  postJson(`/margenes/reglas/${encodeURIComponent(id)}/sustitucion`, cuerpo);

export const cambiarEstadoReglaMargen = (
  id: string,
  activa: boolean,
): Promise<{ regla: ReglaMargen }> =>
  postJson(`/margenes/reglas/${encodeURIComponent(id)}/estado`, { activa });

export const crearPisoMargen = (cuerpo: unknown): Promise<{ piso: PisoMargenVista }> =>
  postJson('/margenes/pisos', cuerpo);

export const sustituirPisoMargen = (
  id: string,
  cuerpo: unknown,
): Promise<{ anterior: PisoMargenVista; nueva: PisoMargenVista }> =>
  postJson(`/margenes/pisos/${encodeURIComponent(id)}/sustitucion`, cuerpo);

export const cambiarEstadoPisoMargen = (
  id: string,
  activa: boolean,
): Promise<{ piso: PisoMargenVista }> =>
  postJson(`/margenes/pisos/${encodeURIComponent(id)}/estado`, { activa });
